import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import React from 'react'
import ReactDOM from 'react-dom'
import { I18nextProvider } from 'react-i18next'
import i18n from 'i18n'
import { removeAccents } from 'helpers'
import Dc3CertificateTemplate from './Dc3CertificateTemplate'
import { HiddenRenderRoot } from './Dc3CertificateTemplate.styled'
import { ensureLocalDc3CertificateTranslations } from './ensureLocalDc3CertificateTranslations'
import {
  IDc3SignatureOverrides,
  normalizeCertificateDataForPdf,
} from './resolveSignatureForPdf'
import { IDc3CertificateData } from './types'

const PDF_WIDTH = 816
const PDF_HEIGHT = 1056
/** ~288 DPI sobre carta 816×1056 px; mejora nitidez de texto e imágenes de firma. */
const PDF_RENDER_SCALE = 3

export interface GenerateDc3PdfOptions {
  signatureOverrides?: IDc3SignatureOverrides
  usePreviewPlaceholder?: boolean
  attestationId?: number
}

const waitForImages = async (element: HTMLElement) => {
  const images = Array.from(element.querySelectorAll('img'))

  await Promise.all(
    images.map((image) =>
      new Promise<void>((resolve, reject) => {
        const finish = () => {
          if (typeof image.decode === 'function') {
            image
              .decode()
              .then(() => resolve())
              .catch(() => resolve())
            return
          }
          resolve()
        }

        if (image.complete && image.naturalWidth > 0) {
          finish()
          return
        }

        image.onload = () => {
          if (image.naturalWidth > 0) {
            finish()
            return
          }
          reject(new Error('signature_image_empty'))
        }
        image.onerror = () => reject(new Error('signature_image_load_failed'))
      }).catch(() => undefined)
    )
  )
}

export const generateDc3PdfFromElement = async (
  element: HTMLElement
): Promise<jsPDF> => {
  await waitForImages(element)

  const canvas = await html2canvas(element, {
    scale: PDF_RENDER_SCALE,
    useCORS: false,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
  })

  const pdf = new jsPDF('portrait', 'px', [PDF_WIDTH, PDF_HEIGHT], true)
  const imgData = canvas.toDataURL('image/png')
  pdf.addImage(imgData, 'PNG', 0, 0, PDF_WIDTH, PDF_HEIGHT)

  return pdf
}

export const generateDc3Pdf = async (
  data: IDc3CertificateData,
  options: GenerateDc3PdfOptions = {}
): Promise<jsPDF> => {
  await ensureLocalDc3CertificateTranslations()
  const pdfData = await normalizeCertificateDataForPdf(
    data,
    options.signatureOverrides,
    options.usePreviewPlaceholder ?? false,
    options.attestationId ?? data.attestation.id
  )

  const container = document.createElement('div')
  document.body.appendChild(container)

  ReactDOM.render(
    <I18nextProvider i18n={i18n}>
      <HiddenRenderRoot>
        <Dc3CertificateTemplate data={pdfData} />
      </HiddenRenderRoot>
    </I18nextProvider>,
    container
  )

  await new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  )

  const target = container.querySelector(
    '[data-dc3-template="true"]'
  ) as HTMLElement | null

  if (!target) {
    ReactDOM.unmountComponentAtNode(container)
    container.remove()
    throw new Error('dc3_template_render_failed')
  }

  try {
    return await generateDc3PdfFromElement(target)
  } finally {
    ReactDOM.unmountComponentAtNode(container)
    container.remove()
  }
}

export const openDc3PdfInNewTab = (pdf: jsPDF, title = 'DC-3 Preview') => {
  pdf.setProperties({ title })
  const blobUrl = String(pdf.output('bloburl'))
  window.open(blobUrl, '_blank', 'noopener,noreferrer')
}

export const downloadDc3Pdf = (pdf: jsPDF, fileName: string) => {
  pdf.save(fileName)
}

export const buildDc3PdfFileName = ({
  fullName,
  identifier,
}: {
  fullName: string
  identifier?: string
}) => {
  const suffix = identifier ? ` (${identifier})` : ''
  return removeAccents(`${fullName}${suffix} - DC-3.pdf`)
}
